import { apiFetch, type ApiOptions } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
  type StockySourceArchiveId,
} from "./stocky_public_ids";

export const STOCKY_DATA_RESOURCES = [
  "tax-types",
  "shop-tax-rates",
  "suppliers",
  "purchase-orders",
  "goods-receipts",
  "stock-adjustments",
  "stocktakes",
  "stocktake-items",
  "reorder-policies",
  "variant-suppliers",
  "source-metadata",
  "product-mismatches",
] as const;

export type StockyDataResource = (typeof STOCKY_DATA_RESOURCES)[number];
/**
 * New backends return canonical strings. Safe numeric IDs remain accepted only
 * while an SPA-first rollout can still talk to the previous backend.
 */
export type StockyDataRecordId = number | string;

const MAX_SIGNED_BIGINT_RECORD_ID = 9_223_372_036_854_775_807n;
const MAX_UNIFIED_SORT_ID = MAX_SIGNED_BIGINT_RECORD_ID * 2n + 1n;
const MAX_RECORD_ID_TEXT_LENGTH = 64;
const UNIFIED_RECORD_ID_PREFIXES: Partial<Record<StockyDataResource, string>> =
  {
    "purchase-orders": "purchase-order",
    "stock-adjustments": "inventory-adjustment",
  };
const DIRECT_RECORD_ID_PREFIXES: Partial<Record<StockyDataResource, string>> = {
  "goods-receipts": "goods-receipt",
};

function isCanonicalPositiveDecimal(value: string, maximum: bigint): boolean {
  return (
    /^[1-9][0-9]*$/.test(value) &&
    value.length <= MAX_RECORD_ID_TEXT_LENGTH &&
    BigInt(value) <= maximum
  );
}

/** Preserve exact route text so a public id is never rounded through Number(). */
export function parseStockyDataRecordId(
  resource: StockyDataResource,
  value: string | undefined,
): StockyDataRecordId | null {
  if (
    value === undefined ||
    value.length === 0 ||
    value.length > MAX_RECORD_ID_TEXT_LENGTH
  ) {
    return null;
  }
  const directPrefix = DIRECT_RECORD_ID_PREFIXES[resource];
  if (directPrefix !== undefined) {
    if (!value.startsWith(`${directPrefix}:`)) return null;
    const sourceId = value.slice(directPrefix.length + 1);
    return isCanonicalPositiveDecimal(sourceId, MAX_SIGNED_BIGINT_RECORD_ID)
      ? value
      : null;
  }
  if (isCanonicalPositiveDecimal(value, MAX_SIGNED_BIGINT_RECORD_ID)) {
    return value;
  }
  const prefix = UNIFIED_RECORD_ID_PREFIXES[resource];
  if (prefix === undefined || !value.startsWith(`${prefix}:`)) return null;
  const sortId = value.slice(prefix.length + 1);
  if (!isCanonicalPositiveDecimal(sortId, MAX_UNIFIED_SORT_ID)) return null;
  return BigInt(sortId) >= 2n ? value : null;
}

export function isStockyDataRecordId(
  resource: StockyDataResource,
  value: unknown,
): value is StockyDataRecordId {
  if (typeof value === "number") {
    return (
      DIRECT_RECORD_ID_PREFIXES[resource] === undefined &&
      Number.isSafeInteger(value) &&
      value > 0
    );
  }
  return (
    typeof value === "string" &&
    parseStockyDataRecordId(resource, value) !== null
  );
}

export function encodeStockyDataRecordId(
  resource: StockyDataResource,
  value: StockyDataRecordId,
): string {
  requireStockyDataRecordId(resource, value);
  return encodeURIComponent(String(value));
}

function requireStockyDataRecordId(
  resource: StockyDataResource,
  value: StockyDataRecordId,
): StockyDataRecordId {
  if (!isStockyDataRecordId(resource, value)) {
    throw new TypeError("invalid Stocky data record id");
  }
  return value;
}

function canonicalStockyDataRecordId(
  resource: StockyDataResource,
  value: unknown,
): StockyDataRecordId {
  if (!isStockyDataRecordId(resource, value)) {
    throw new TypeError("invalid Stocky data record id");
  }
  return String(value);
}

const CHILD_RECORD_ID_PREFIXES = {
  "purchase-orders": "purchase-order-item",
  "goods-receipts": "goods-receipt-item",
  "stock-adjustments": "inventory-adjustment-item",
} as const;

type StockyDataChildResource =
  keyof typeof CHILD_RECORD_ID_PREFIXES | "source-metadata";

function isStockyDataChildResource(
  resource: StockyDataResource,
): resource is StockyDataChildResource {
  return resource === "source-metadata" || resource in CHILD_RECORD_ID_PREFIXES;
}

/**
 * Child ids use a different namespace from their parent record. Retained PO /
 * adjustment children carry an even unified parent id plus a 1-based row
 * position; operational children carry one odd unified item id. Generic source
 * rows use canonical string namespaces. During SPA-first rollout the old
 * backend can still emit positive safe integer ids for PO, adjustment, and
 * generic-source children; canonicalize those exact integers to strings.
 */
export function parseStockyDataChildRecordId(
  resource: StockyDataResource,
  value: number | string | undefined,
): StockyDataRecordId | null {
  if (!isStockyDataChildResource(resource)) return null;
  if (resource === "source-metadata") {
    if (typeof value === "number" && Number.isSafeInteger(value) && value > 0) {
      return String(value);
    }
    return typeof value === "string" &&
      isCanonicalPositiveDecimal(value, MAX_SIGNED_BIGINT_RECORD_ID)
      ? value
      : null;
  }
  if (
    (resource === "purchase-orders" || resource === "stock-adjustments") &&
    typeof value === "number"
  ) {
    return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
  }
  if (typeof value !== "string" || value.length > 128) return null;
  const prefix = CHILD_RECORD_ID_PREFIXES[resource];
  if (!value.startsWith(`${prefix}:`)) return null;
  const parts = value.slice(prefix.length + 1).split(":");
  if (resource === "goods-receipts") {
    return parts.length === 1 &&
      isCanonicalPositiveDecimal(parts[0], MAX_SIGNED_BIGINT_RECORD_ID)
      ? value
      : null;
  }
  if (parts.length === 1) {
    if (!isCanonicalPositiveDecimal(parts[0], MAX_UNIFIED_SORT_ID)) {
      return null;
    }
    const operationalItemId = BigInt(parts[0]);
    return operationalItemId >= 3n && operationalItemId % 2n === 1n
      ? value
      : null;
  }
  if (
    parts.length !== 2 ||
    !isCanonicalPositiveDecimal(parts[0], MAX_UNIFIED_SORT_ID) ||
    !isCanonicalPositiveDecimal(parts[1], MAX_SIGNED_BIGINT_RECORD_ID)
  ) {
    return null;
  }
  const retainedParentId = BigInt(parts[0]);
  return retainedParentId >= 2n && retainedParentId % 2n === 0n ? value : null;
}

function canonicalStockyDataChildRecordId(
  resource: StockyDataResource,
  value: unknown,
): StockyDataRecordId {
  const parsed = parseStockyDataChildRecordId(
    resource,
    value as number | string | undefined,
  );
  if (parsed === null) {
    throw new TypeError("invalid Stocky data child record id");
  }
  return String(parsed);
}

export type StockyPurchaseOrderStatusFilter =
  "all" | "draft" | "ordered" | "archived" | "awaiting_payment" | "overdue";

export function isStockyDataResource(
  value: string | undefined,
): value is StockyDataResource {
  return (
    value !== undefined &&
    (STOCKY_DATA_RESOURCES as readonly string[]).includes(value)
  );
}

export type StockyDataMaskTier = "none" | "plan";

export interface StockyDataFieldDisplay {
  key: string;
  state: "present" | "missing" | "unknown" | "masked";
  /** Server-rendered text. Never derive a display value from source data. */
  display_text: string;
  masking: {
    tier: StockyDataMaskTier;
    reason: string | null;
  };
}

export interface StockyDataPage {
  limit: number;
  count: number;
  snapshot_total: number;
  next_cursor: string | null;
  high_watermark: number | string;
}

export interface StockyDataRecordLinks {
  self: string | null;
  children: string | null;
  note_revisions: string | null;
}

export interface StockyDataSourceDownload {
  id: StockySourceArchiveId;
  filename: string;
  sha256: string;
  size_bytes: number;
  created_at: string | null;
  links: {
    download: string;
  };
}

export interface StockyHistorySourceRow {
  source_archive_id: StockySourceArchiveId;
  source_row_number: number;
  filename: string;
  fields: StockyDataFieldDisplay[];
  links: {
    preview: string;
    download: string;
  };
}

export type StockyNoteKind =
  | "supplier_private_note"
  | "supplier_po_note_template"
  | "purchase_order_note"
  | "internal_note";

export interface StockyDataNoteSlot {
  kind: StockyNoteKind;
  latest_revision: number;
  has_value: boolean;
  can_edit: boolean;
  links: { revisions: string };
}

export interface StockyDataRecord {
  id: StockyDataRecordId;
  primary_text: string;
  fields: StockyDataFieldDisplay[];
  masked_fields: string[];
  links: StockyDataRecordLinks;
  updated_at: string | null;
  note_slots: StockyDataNoteSlot[] | null;
}

export interface StockyDataCatalogResource {
  key: StockyDataResource;
  label: string;
  description: string;
  count: number;
  masked: boolean;
  required_plan: string | null;
  allowed_filters: string[];
  note_kinds: StockyNoteKind[];
  links: { list: string };
}

export interface StockyDataCatalog {
  resources: StockyDataCatalogResource[];
}

export interface StockyPurchaseOrderCurrencyAmount {
  currency: string | null;
  amount: string;
}

export interface StockyPurchaseOrderMoneyAggregate {
  count: number;
  known_amount_count: number;
  unknown_amount_count: number;
  amounts: StockyPurchaseOrderCurrencyAmount[];
}

export interface StockyPurchaseOrderPaymentBucket extends StockyPurchaseOrderMoneyAggregate {
  week_start: string;
}

export interface StockyPurchaseOrderDashboardSummary {
  as_of: string;
  horizon_weeks: number;
  statuses: Record<
    StockyPurchaseOrderStatusFilter,
    StockyPurchaseOrderMoneyAggregate
  >;
  upcoming: {
    buckets: StockyPurchaseOrderPaymentBucket[];
    beyond_horizon: StockyPurchaseOrderMoneyAggregate;
    missing_due_date: StockyPurchaseOrderMoneyAggregate;
  };
  calculation: {
    amount_basis: "stocky_supplier_cost_subtotal_plus_adjustments_and_shipping";
    rounding: "each_line_half_up_2dp_before_sum";
    unknown_values_are_zero: false;
  };
}

export interface StockyDataRecordList {
  resource: StockyDataResource;
  records: StockyDataRecord[];
  masked_fields: string[];
  mask: StockyDataPlanMask | null;
  page: StockyDataPage;
}

export interface StockyDataPlanMask {
  masked: true;
  count: number;
  reason: string;
  required_plan: string;
  /** Schema-only allowlist for columns rendered with synthetic silhouettes. */
  preview_fields?: string[];
}

export interface StockyDataDetail {
  resource: StockyDataResource;
  record: StockyDataRecord | null;
  source_downloads: StockyDataSourceDownload[];
  source_rows: StockyHistorySourceRow[];
  mask: StockyDataPlanMask | null;
}

export interface StockyDataGenericSource {
  status: "available" | "masked" | "unavailable";
  filename: string;
  category: string;
  context: Record<string, string>;
  headers: string[];
  row_count: number | null;
  reason: string | null;
  required_plan: string | null;
}

export interface StockyDataChildren extends StockyDataRecordList {
  parent_id: StockyDataRecordId;
  generic_source: StockyDataGenericSource | null;
}

export interface StockyNoteRevision {
  id: StockyPublicBigIntId;
  kind: StockyNoteKind;
  operation: "set" | "clear";
  display_text: string;
  revision: number;
  created_at: string;
}

export interface StockyNoteRevisionList {
  resource: "suppliers" | "purchase-orders";
  record_id: StockyDataRecordId;
  revisions: StockyNoteRevision[];
  page: StockyDataPage;
}

export interface CreateStockyNoteRevisionInput {
  kind: StockyNoteKind;
  operation: "set" | "clear";
  text: string | null;
  expected_revision: number;
}

export interface CreateStockyNoteRevisionResponse {
  revision: StockyNoteRevision;
  replayed: boolean;
}

export interface StockyProductIdentity {
  shopify_product_id: string;
  shopify_variant_id: string;
  shopify_inventory_item_id: string | null;
  sku: string | null;
  variant_title: string | null;
  product_title: string;
}

export interface StockyCurrentVariant extends StockyProductIdentity {
  /** UreyukiBox's internal target key. Mapping writes must use this value. */
  variant_id: string;
  current: boolean;
  orderable: boolean;
}

export interface StockyProductTargetSnapshot extends StockyProductIdentity {
  variant_id: string;
}

export interface StockyProductMappingRevision {
  id: string;
  source_variant_id: string;
  decision: "mapped" | "not_current";
  target_variant_id: string | null;
  revision: number;
  mapping_provenance:
    | "legacy"
    | "merchant_manual_v1"
    | "draft_catalog_receipt_v1";
  operational_application_version: 1 | null;
  supersedes_id: string | null;
  created_at: string;
  target_snapshot: StockyProductTargetSnapshot | null;
}

export interface StockyProductMismatch {
  source_variant_id: string;
  source: StockyProductIdentity;
  /** CAS epoch for the exact retained source row shown to the merchant. */
  source_snapshot_revision: string;
  mismatch_reasons: Array<
    "product_absent_from_shopify" | "variant_absent_from_shopify"
  >;
  related_counts: {
    variant_supplier_conditions: number;
    reorder_policies: number;
    stocktake_items: number;
  };
  resolution_status:
    "unresolved" | "auto_matched" | "mapped" | "not_current" | "target_stale";
  resolution_reason: string;
  auto_match_basis:
    | "shopify_variant_id"
    | "shopify_inventory_item_id"
    | "unique_exact_sku"
    | null;
  current_target: StockyCurrentVariant | null;
  latest_revision: StockyProductMappingRevision | null;
  operational_application_status:
    | "not_applicable"
    | "mapping_required"
    | "confirmation_required"
    | "applied"
    | "legacy_unapplied"
    | "source_changed"
    | "draft_mapping_settings_not_copied";
  /** Server-owned, reload-safe Shopify DRAFT creation/reconciliation state. */
  draft_product: StockyMismatchDraftProduct | null;
}

export interface StockyMismatchDraftProduct {
  claim_token: string;
  state: "not_started" | "creating" | "ready" | "catalog_synced";
  shopify_product_id: string | null;
  shopify_variant_id: string | null;
  shopify_product_gid: string | null;
  shopify_variant_gid: string | null;
  edit_required: boolean;
  current_target: StockyCurrentVariant | null;
  /** True only for a catalog-synced v1 claim; the merchant must still confirm. */
  legacy_mapping_upgrade_available: boolean;
}

export interface CreateStockyMismatchDraftProductInput {
  source: StockyProductIdentity;
  source_snapshot_revision: string;
  expected_mapping_revision: number;
}

export interface SyncStockyMismatchDraftProductInput
  extends CreateStockyMismatchDraftProductInput {
  claim_token: string;
}

export interface StockyMismatchDraftProductResponse {
  source_variant_id: string;
  source_snapshot_revision: string;
  expected_mapping_revision: number;
  draft_product: StockyMismatchDraftProduct;
  mapping_written: boolean;
  inventory_written: false;
  quantity_written: false;
}

export type StockyMismatchDraftCatalogSyncResponse =
  StockyMismatchDraftProductResponse;

/** The body is intentionally identical to sync: the server receives only the
 * frozen claim context and derives the target from its verified v1 receipt. */
export type CompleteLegacyStockyMismatchDraftMappingInput =
  SyncStockyMismatchDraftProductInput;

export interface StockyProductMismatchList {
  records: StockyProductMismatch[];
  status: StockyProductMismatchStatus;
  counts: {
    pending: number;
    resolved: number;
    total: number;
  };
  page: {
    limit: number;
    count: number;
    next_after_id: string | null;
    total: number | null;
  };
}

export type StockyProductMismatchStatus = "pending" | "resolved" | "all";

export interface StockyCurrentVariantSearch {
  variants: StockyCurrentVariant[];
  count: number;
}

export interface StockyProductMappingRevisionList {
  source_variant_id: string;
  revisions: StockyProductMappingRevision[];
}

export interface CreateStockyProductMappingInput {
  decision: "mapped" | "not_current";
  target_variant_id: StockyPublicBigIntId | null;
  expected_revision: number;
}

export interface CreateStockyProductMappingResponse {
  revision: StockyProductMappingRevision;
  replayed: boolean;
  current_target: StockyCurrentVariant | null;
  inventory_written: false;
  operational_settings_applied: boolean;
}

function nullableCanonicalProductId(value: unknown): string | null {
  return value === null ? null : canonicalStockyPublicBigIntId(value);
}

function normalizeStockyProductIdentity(value: unknown): StockyProductIdentity {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky product identity");
  }
  const row = value as StockyProductIdentity & {
    shopify_product_id: unknown;
    shopify_variant_id: unknown;
    shopify_inventory_item_id: unknown;
  };
  return {
    ...row,
    shopify_product_id: canonicalStockyPublicBigIntId(row.shopify_product_id),
    shopify_variant_id: canonicalStockyPublicBigIntId(row.shopify_variant_id),
    shopify_inventory_item_id: nullableCanonicalProductId(
      row.shopify_inventory_item_id,
    ),
  };
}

function normalizeStockyCurrentVariant(value: unknown): StockyCurrentVariant {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky current variant");
  }
  const row = value as StockyCurrentVariant & { variant_id: unknown };
  return {
    ...row,
    ...normalizeStockyProductIdentity(row),
    variant_id: canonicalStockyPublicBigIntId(row.variant_id),
  };
}

function canonicalStockyMismatchDraftClaimToken(value: unknown): string {
  if (
    typeof value !== "string" ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
      value,
    )
  ) {
    throw new TypeError("invalid Stocky mismatch draft claim token");
  }
  return value;
}

function canonicalStockyMappingRevision(value: unknown): number {
  if (!Number.isSafeInteger(value) || Number(value) < 0) {
    throw new TypeError("invalid Stocky product mapping revision");
  }
  return Number(value);
}

function canonicalStockyMismatchSnapshotRevision(value: unknown): string {
  if (
    typeof value !== "string" ||
    (value !== "0" &&
      !isCanonicalPositiveDecimal(value, MAX_SIGNED_BIGINT_RECORD_ID))
  ) {
    throw new TypeError("invalid Stocky mismatch source snapshot revision");
  }
  return value;
}

function normalizeStockyMismatchDraftGid(
  value: unknown,
  resource: "Product" | "ProductVariant",
  expectedId: string | null,
): string | null {
  if (expectedId === null) {
    if (value !== null) {
      throw new TypeError("invalid Stocky mismatch draft Shopify GID");
    }
    return null;
  }
  const expected = `gid://shopify/${resource}/${expectedId}`;
  if (value !== expected) {
    throw new TypeError("invalid Stocky mismatch draft Shopify GID");
  }
  return expected;
}

function normalizeStockyMismatchDraftProduct(
  value: unknown,
): StockyMismatchDraftProduct {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("invalid Stocky mismatch draft product");
  }
  const draft = value as StockyMismatchDraftProduct & {
    claim_token: unknown;
    shopify_product_id: unknown;
    shopify_variant_id: unknown;
    shopify_product_gid: unknown;
    shopify_variant_gid: unknown;
    current_target: unknown;
  };
  if (
    draft.state !== "not_started" &&
    draft.state !== "creating" &&
    draft.state !== "ready" &&
    draft.state !== "catalog_synced"
  ) {
    throw new TypeError("invalid Stocky mismatch draft state");
  }
  if (typeof draft.edit_required !== "boolean") {
    throw new TypeError("invalid Stocky mismatch draft edit state");
  }
  if (typeof draft.legacy_mapping_upgrade_available !== "boolean") {
    throw new TypeError("invalid Stocky mismatch draft upgrade state");
  }
  const productId = nullableCanonicalProductId(draft.shopify_product_id);
  const variantId = nullableCanonicalProductId(draft.shopify_variant_id);
  const productGid = normalizeStockyMismatchDraftGid(
    draft.shopify_product_gid,
    "Product",
    productId,
  );
  const variantGid = normalizeStockyMismatchDraftGid(
    draft.shopify_variant_gid,
    "ProductVariant",
    variantId,
  );
  const currentTarget =
    draft.current_target === null
      ? null
      : normalizeStockyCurrentVariant(draft.current_target);
  const ready = draft.state === "ready" || draft.state === "catalog_synced";
  const hasCompleteReceipt =
    productId !== null &&
    variantId !== null &&
    productGid !== null &&
    variantGid !== null;
  const hasAnyReceipt =
    productId !== null ||
    variantId !== null ||
    productGid !== null ||
    variantGid !== null;
  if (
    (ready ? !hasCompleteReceipt : hasAnyReceipt) ||
    draft.edit_required !== (draft.state === "ready") ||
    (draft.legacy_mapping_upgrade_available &&
      draft.state !== "catalog_synced") ||
    (draft.state === "catalog_synced") !== (currentTarget !== null) ||
    (currentTarget !== null &&
      (!currentTarget.current ||
        currentTarget.shopify_product_id !== productId ||
        currentTarget.shopify_variant_id !== variantId))
  ) {
    throw new TypeError("inconsistent Stocky mismatch draft product");
  }
  return {
    claim_token: canonicalStockyMismatchDraftClaimToken(draft.claim_token),
    state: draft.state,
    shopify_product_id: productId,
    shopify_variant_id: variantId,
    shopify_product_gid: productGid,
    shopify_variant_gid: variantGid,
    edit_required: draft.edit_required,
    current_target: currentTarget,
    legacy_mapping_upgrade_available: draft.legacy_mapping_upgrade_available,
  };
}

function normalizeStockyProductTargetSnapshot(
  value: unknown,
): StockyProductTargetSnapshot {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky product mapping snapshot");
  }
  const row = value as StockyProductTargetSnapshot & { variant_id: unknown };
  return {
    ...row,
    ...normalizeStockyProductIdentity(row),
    variant_id: canonicalStockyPublicBigIntId(row.variant_id),
  };
}

function normalizeStockyProductMappingRevision(
  value: unknown,
): StockyProductMappingRevision {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky product mapping revision");
  }
  const row = value as StockyProductMappingRevision & {
    id: unknown;
    source_variant_id: unknown;
    target_variant_id: unknown;
    supersedes_id: unknown;
    target_snapshot: unknown;
  };
  if (
    row.mapping_provenance !== "legacy" &&
    row.mapping_provenance !== "merchant_manual_v1" &&
    row.mapping_provenance !== "draft_catalog_receipt_v1"
  ) {
    throw new TypeError("invalid Stocky product mapping provenance");
  }
  return {
    id: canonicalStockyPublicBigIntId(row.id),
    source_variant_id: canonicalStockyPublicBigIntId(row.source_variant_id),
    decision: row.decision,
    target_variant_id: nullableCanonicalProductId(row.target_variant_id),
    revision: row.revision,
    mapping_provenance: row.mapping_provenance,
    operational_application_version: row.operational_application_version,
    supersedes_id: nullableCanonicalProductId(row.supersedes_id),
    created_at: row.created_at,
    target_snapshot:
      row.target_snapshot === null
        ? null
        : normalizeStockyProductTargetSnapshot(row.target_snapshot),
  };
}

function normalizeStockyProductMismatch(value: unknown): StockyProductMismatch {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky product mismatch");
  }
  const row = value as StockyProductMismatch & {
    source_variant_id: unknown;
    source: unknown;
    source_snapshot_revision: unknown;
    current_target: unknown;
    latest_revision: unknown;
    draft_product: unknown;
  };
  return {
    source_variant_id: canonicalStockyPublicBigIntId(row.source_variant_id),
    source: normalizeStockyProductIdentity(row.source),
    source_snapshot_revision: canonicalStockyMismatchSnapshotRevision(
      row.source_snapshot_revision,
    ),
    mismatch_reasons: row.mismatch_reasons,
    related_counts: row.related_counts,
    resolution_status: row.resolution_status,
    resolution_reason: row.resolution_reason,
    auto_match_basis: row.auto_match_basis,
    current_target:
      row.current_target === null
        ? null
        : normalizeStockyCurrentVariant(row.current_target),
    latest_revision:
      row.latest_revision === null
        ? null
        : normalizeStockyProductMappingRevision(row.latest_revision),
    operational_application_status: row.operational_application_status,
    draft_product:
      row.draft_product === null
        ? null
        : normalizeStockyMismatchDraftProduct(row.draft_product),
  };
}

function normalizeStockyProductMismatchList(
  value: unknown,
): StockyProductMismatchList {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky product mismatch list");
  }
  const response = value as StockyProductMismatchList & {
    records: unknown;
    page: StockyProductMismatchList["page"] & { next_after_id: unknown };
  };
  if (!Array.isArray(response.records) || !response.page) {
    throw new TypeError("invalid Stocky product mismatch list");
  }
  return {
    records: response.records.map(normalizeStockyProductMismatch),
    status: response.status,
    counts: response.counts,
    page: {
      limit: response.page.limit,
      count: response.page.count,
      next_after_id: nullableCanonicalProductId(response.page.next_after_id),
      total: response.page.total,
    },
  };
}

function normalizeStockyCurrentVariantSearch(
  value: unknown,
): StockyCurrentVariantSearch {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky current variant search");
  }
  const response = value as StockyCurrentVariantSearch & { variants: unknown };
  if (!Array.isArray(response.variants)) {
    throw new TypeError("invalid Stocky current variant search");
  }
  return {
    ...response,
    variants: response.variants.map(normalizeStockyCurrentVariant),
  };
}

function normalizeStockyProductMappingRevisionList(
  value: unknown,
): StockyProductMappingRevisionList {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky product mapping revision list");
  }
  const response = value as StockyProductMappingRevisionList & {
    source_variant_id: unknown;
    revisions: unknown;
  };
  if (!Array.isArray(response.revisions)) {
    throw new TypeError("invalid Stocky product mapping revision list");
  }
  return {
    ...response,
    source_variant_id: canonicalStockyPublicBigIntId(
      response.source_variant_id,
    ),
    revisions: response.revisions.map(normalizeStockyProductMappingRevision),
  };
}

function normalizeCreateStockyProductMappingResponse(
  value: unknown,
): CreateStockyProductMappingResponse {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky product mapping response");
  }
  const response = value as CreateStockyProductMappingResponse & {
    revision: unknown;
    current_target: unknown;
  };
  if (
    typeof response.replayed !== "boolean" ||
    response.inventory_written !== false ||
    typeof response.operational_settings_applied !== "boolean" ||
    response.current_target === undefined
  ) {
    throw new TypeError("invalid Stocky product mapping response contract");
  }
  const revision = normalizeStockyProductMappingRevision(response.revision);
  if (
    revision.operational_application_version !== null &&
    revision.operational_application_version !== 1
  ) {
    throw new TypeError("invalid Stocky operational application version");
  }
  if (
    response.operational_settings_applied &&
    revision.operational_application_version !== 1
  ) {
    throw new TypeError("inconsistent Stocky operational application response");
  }
  return {
    ...response,
    revision,
    current_target:
      response.current_target === null
        ? null
        : normalizeStockyCurrentVariant(response.current_target),
  };
}

const ROOT = "/api/stocky-data";
const STOCKY_DATA_CONTRACT_QUERY = "stocky_data_contract";
const STOCKY_DATA_CONTRACT_VERSION = "namespaced-v1";
const STOCKY_PRODUCT_MAPPING_CONTRACT_VERSION =
  "namespaced-operational-copy-once-v1";
const STOCKY_MISMATCH_DRAFT_CONTRACT_VERSION =
  "namespaced-draft-auto-map-v2";

/**
 * Add the public response-shape contract exactly once.
 *
 * This deliberately uses a query parameter instead of a custom header. The
 * SPA must be safe to deploy before the enforcing backend, and an older
 * cross-origin backend would reject a new request header during CORS
 * preflight before it had a chance to ignore it.
 */
function withStockyDataContract(
  path: string,
  contractVersion = STOCKY_DATA_CONTRACT_VERSION,
): string {
  const queryStart = path.indexOf("?");
  if (
    queryStart >= 0 &&
    new URLSearchParams(path.slice(queryStart + 1)).has(
      STOCKY_DATA_CONTRACT_QUERY,
    )
  ) {
    throw new TypeError("duplicate Stocky data contract");
  }
  const separator = queryStart >= 0 ? "&" : "?";
  return `${path}${separator}${STOCKY_DATA_CONTRACT_QUERY}=${encodeURIComponent(contractVersion)}`;
}

function stockyDataFetch<T>(
  path: string,
  opts: ApiOptions = {},
  contractVersion = STOCKY_DATA_CONTRACT_VERSION,
): Promise<T> {
  return apiFetch<T>(withStockyDataContract(path, contractVersion), opts);
}

function canonicalNonnegativePublicInteger(value: unknown): string {
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new TypeError("invalid Stocky data high watermark");
    }
    return String(value);
  }
  if (
    typeof value !== "string" ||
    !/^(?:0|[1-9][0-9]*)$/.test(value) ||
    value.length > MAX_RECORD_ID_TEXT_LENGTH ||
    BigInt(value) > MAX_UNIFIED_SORT_ID
  ) {
    throw new TypeError("invalid Stocky data high watermark");
  }
  return value;
}

function normalizeStockyDataPage(value: unknown): StockyDataPage {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky data page");
  }
  const page = value as StockyDataPage & { high_watermark: unknown };
  return {
    ...page,
    high_watermark: canonicalNonnegativePublicInteger(page.high_watermark),
  };
}

function normalizeStockyDataSourceDownload(
  value: unknown,
): StockyDataSourceDownload {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky source download");
  }
  const archive = value as StockyDataSourceDownload & { id: unknown };
  return {
    id: canonicalStockyPublicBigIntId(archive.id),
    filename: archive.filename,
    sha256: archive.sha256,
    size_bytes: archive.size_bytes,
    created_at: archive.created_at,
    links: { download: archive.links.download },
  };
}

function normalizeStockyHistorySourceRow(value: unknown): StockyHistorySourceRow {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky history source row");
  }
  const row = value as StockyHistorySourceRow & { source_archive_id: unknown };
  const archiveId = canonicalStockyPublicBigIntId(row.source_archive_id);
  if (
    !Number.isSafeInteger(row.source_row_number) ||
    row.source_row_number < 1 ||
    typeof row.filename !== "string" ||
    row.filename.length === 0 ||
    row.links?.preview !==
      `/api/stocky-data/source-metadata/${archiveId}/children` ||
    row.links?.download !==
      `/api/migrate/portability/sources/${archiveId}/download`
  ) {
    throw new TypeError("invalid Stocky history source row");
  }
  return {
    source_archive_id: archiveId,
    source_row_number: row.source_row_number,
    filename: row.filename,
    fields: normalizeStockyDataFields(row.fields),
    links: { ...row.links },
  };
}

function normalizeStockyDataNoteSlots(
  value: unknown,
): StockyDataNoteSlot[] | null {
  if (value === null) return null;
  if (!Array.isArray(value)) {
    throw new TypeError("invalid Stocky note slots");
  }
  return value.map((item) => {
    if (item === null || typeof item !== "object") {
      throw new TypeError("invalid Stocky note slot");
    }
    const slot = item as StockyDataNoteSlot;
    if (
      !["supplier_private_note", "supplier_po_note_template", "purchase_order_note", "internal_note"].includes(
        slot.kind,
      ) ||
      !Number.isSafeInteger(slot.latest_revision) ||
      slot.latest_revision < 0 ||
      typeof slot.has_value !== "boolean" ||
      typeof slot.can_edit !== "boolean" ||
      typeof slot.links?.revisions !== "string"
    ) {
      throw new TypeError("invalid Stocky note slot");
    }
    return {
      kind: slot.kind,
      latest_revision: slot.latest_revision,
      has_value: slot.has_value,
      can_edit: slot.can_edit,
      links: { revisions: slot.links.revisions },
    };
  });
}

const INTERNAL_STOCKY_DATA_FIELD_KEYS = new Set([
  "source_import_id",
  "evidence_fidelity",
  "coverage_status",
  "normalization_level",
  "source_archive_id",
  "source_locator",
  "source_note_revision_id",
  "actor_shopify_user_id",
  "body_sha256",
  "supersedes_id",
  "request_sha256",
  "provenance",
  "source_mapping",
  "source_input_revision",
  "archive_by_source_import_id",
  "api_migration_evidence",
]);

function normalizeStockyDataFields(value: unknown): StockyDataFieldDisplay[] {
  if (!Array.isArray(value)) {
    throw new TypeError("invalid Stocky data fields");
  }
  return value.flatMap((item) => {
    if (item === null || typeof item !== "object") {
      throw new TypeError("invalid Stocky data field");
    }
    const field = item as StockyDataFieldDisplay;
    if (INTERNAL_STOCKY_DATA_FIELD_KEYS.has(field.key)) return [];
    if (
      typeof field.key !== "string" ||
      !["present", "missing", "unknown", "masked"].includes(field.state) ||
      typeof field.display_text !== "string" ||
      !["none", "plan"].includes(field.masking?.tier) ||
      (field.masking.reason !== null &&
        typeof field.masking.reason !== "string")
    ) {
      throw new TypeError("invalid Stocky data field");
    }
    return [
      {
        key: field.key,
        state: field.state,
        display_text: field.display_text,
        masking: {
          tier: field.masking.tier,
          reason: field.masking.reason,
        },
      },
    ];
  });
}

function normalizeStockyDataRecord(
  resource: StockyDataResource,
  value: unknown,
): StockyDataRecord {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky data record");
  }
  const record = value as StockyDataRecord & {
    id: unknown;
  };
  return {
    id: canonicalStockyDataRecordId(resource, record.id),
    primary_text: record.primary_text,
    fields: normalizeStockyDataFields(record.fields),
    masked_fields: record.masked_fields,
    links: record.links,
    updated_at: record.updated_at,
    note_slots: normalizeStockyDataNoteSlots(record.note_slots),
  };
}

function normalizeStockyDataChildRecord(
  resource: StockyDataResource,
  value: unknown,
): StockyDataRecord {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky data child record");
  }
  const record = value as StockyDataRecord & {
    id: unknown;
  };
  return {
    id: canonicalStockyDataChildRecordId(resource, record.id),
    primary_text: record.primary_text,
    fields: normalizeStockyDataFields(record.fields),
    masked_fields: record.masked_fields,
    links: record.links,
    updated_at: record.updated_at,
    note_slots: normalizeStockyDataNoteSlots(record.note_slots),
  };
}

function normalizeStockyDataGenericSource(
  value: unknown,
): StockyDataGenericSource | null {
  if (value === null) return null;
  if (typeof value !== "object") {
    throw new TypeError("invalid Stocky generic source");
  }
  const source = value as StockyDataGenericSource;
  if (
    !["available", "masked", "unavailable"].includes(source.status) ||
    typeof source.filename !== "string" ||
    typeof source.category !== "string" ||
    source.context === null ||
    typeof source.context !== "object" ||
    Array.isArray(source.context) ||
    Object.values(source.context).some((item) => typeof item !== "string") ||
    !Array.isArray(source.headers) ||
    source.headers.some((header) => typeof header !== "string") ||
    (source.row_count !== null &&
      (!Number.isSafeInteger(source.row_count) || source.row_count < 0)) ||
    (source.reason !== null && typeof source.reason !== "string") ||
    (source.required_plan !== null && typeof source.required_plan !== "string")
  ) {
    throw new TypeError("invalid Stocky generic source");
  }
  return {
    status: source.status,
    filename: source.filename,
    category: source.category,
    context: { ...source.context },
    headers: [...source.headers],
    row_count: source.row_count,
    reason: source.reason,
    required_plan: source.required_plan,
  };
}

function normalizeStockyDataRecordList(
  resource: StockyDataResource,
  value: unknown,
): StockyDataRecordList {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky data record list");
  }
  const response = value as StockyDataRecordList & {
    records: unknown;
    page: unknown;
  };
  if (response.resource !== resource || !Array.isArray(response.records)) {
    throw new TypeError("invalid Stocky data record list");
  }
  return {
    ...response,
    records: response.records.map((record) =>
      normalizeStockyDataRecord(resource, record),
    ),
    page: normalizeStockyDataPage(response.page),
  };
}

function normalizeStockyNoteRevision(value: unknown): StockyNoteRevision {
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky note revision");
  }
  const revision = value as StockyNoteRevision & { id: unknown };
  return {
    id: canonicalStockyPublicBigIntId(revision.id),
    kind: revision.kind,
    operation: revision.operation,
    display_text: revision.display_text,
    revision: revision.revision,
    created_at: revision.created_at,
  };
}

export type StockyDataExportFormat = "csv" | "pdf";
export type StockyDataChildExportResource =
  "purchase-orders" | "goods-receipts" | "stock-adjustments" | "stocktakes";

const MAX_STOCKY_DATA_EXPORT_BYTES: Record<StockyDataExportFormat, number> = {
  csv: 64 * 1024 * 1024,
  pdf: 16 * 1024 * 1024,
};

export function getStockyDataCatalog(
  expectedShopDomain: string,
): Promise<StockyDataCatalog> {
  return stockyDataFetch(`${ROOT}/catalog`, { expectedShopDomain });
}

export async function downloadStockyDataExport(
  resource: StockyDataResource,
  exportFormat: StockyDataExportFormat,
  input: {
    q?: string;
    parentId?: StockyDataRecordId | null;
    status?: StockyPurchaseOrderStatusFilter;
  },
  expectedShopDomain: string,
) {
  const { downloadVerifiedBoundedFile } = await import("./stocky_portability");
  const query = new URLSearchParams({ format: exportFormat });
  if (input.q?.trim()) query.set("q", input.q.trim());
  if (input.parentId !== null && input.parentId !== undefined) {
    query.set(
      "parent_id",
      String(requireStockyDataRecordId("stocktakes", input.parentId)),
    );
  }
  if (input.status && input.status !== "all") {
    query.set("status", input.status);
  }
  return downloadVerifiedBoundedFile(
    withStockyDataContract(`${ROOT}/${resource}/export?${query.toString()}`),
    {
      maximumBytes: MAX_STOCKY_DATA_EXPORT_BYTES[exportFormat],
      fallbackFilename: `ureyukibox-stocky-data-${resource}.${exportFormat}`,
      expectedShopDomain,
    },
  );
}

export async function downloadStockyDataChildrenExport(
  resource: StockyDataChildExportResource,
  recordId: StockyDataRecordId,
  exportFormat: StockyDataExportFormat,
  expectedShopDomain: string,
) {
  const { downloadVerifiedBoundedFile } = await import("./stocky_portability");
  const encodedRecordId = encodeStockyDataRecordId(resource, recordId);
  return downloadVerifiedBoundedFile(
    withStockyDataContract(
      `${ROOT}/${resource}/${encodedRecordId}/children/export?format=${exportFormat}`,
    ),
    {
      maximumBytes: MAX_STOCKY_DATA_EXPORT_BYTES[exportFormat],
      fallbackFilename: `ureyukibox-data-${resource}-details.${exportFormat}`,
      expectedShopDomain,
    },
  );
}

export async function listStockyDataRecords(
  resource: StockyDataResource,
  input: {
    limit?: number;
    cursor?: string | null;
    q?: string;
    parentId?: StockyDataRecordId | null;
    status?: StockyPurchaseOrderStatusFilter;
  },
  expectedShopDomain: string,
): Promise<StockyDataRecordList> {
  const response = await stockyDataFetch<unknown>(`${ROOT}/${resource}`, {
    expectedShopDomain,
    query: {
      limit: input.limit ?? 50,
      cursor: input.cursor ?? undefined,
      q: input.q?.trim() || undefined,
      parent_id:
        input.parentId === null || input.parentId === undefined
          ? undefined
          : requireStockyDataRecordId("stocktakes", input.parentId),
      status: input.status && input.status !== "all" ? input.status : undefined,
    },
  });
  return normalizeStockyDataRecordList(resource, response);
}

export function getStockyPurchaseOrderDashboardSummary(
  expectedShopDomain: string,
  horizonWeeks = 8,
): Promise<StockyPurchaseOrderDashboardSummary> {
  return stockyDataFetch(`${ROOT}/purchase-orders/summary`, {
    expectedShopDomain,
    query: { horizon_weeks: horizonWeeks },
  });
}

export async function getStockyDataRecord(
  resource: StockyDataResource,
  recordId: StockyDataRecordId,
  expectedShopDomain: string,
): Promise<StockyDataDetail> {
  const encodedRecordId = encodeStockyDataRecordId(resource, recordId);
  const value = await stockyDataFetch<unknown>(
    `${ROOT}/${resource}/${encodedRecordId}`,
    {
      expectedShopDomain,
    },
  );
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky data detail");
  }
  const response = value as StockyDataDetail & {
    record: unknown;
    source_downloads: unknown;
    source_rows: unknown;
  };
  if (
    response.resource !== resource ||
    !Array.isArray(response.source_downloads) ||
    (response.source_rows !== undefined && !Array.isArray(response.source_rows))
  ) {
    throw new TypeError("invalid Stocky data detail");
  }
  return {
    resource,
    record:
      response.record === null
        ? null
        : normalizeStockyDataRecord(resource, response.record),
    source_downloads: response.source_downloads.map(
      normalizeStockyDataSourceDownload,
    ),
    source_rows: Array.isArray(response.source_rows)
      ? response.source_rows.map(normalizeStockyHistorySourceRow)
      : [],
    mask: response.mask,
  };
}

export async function listStockyDataChildren(
  resource: StockyDataResource,
  recordId: StockyDataRecordId,
  input: { limit?: number; cursor?: string | null },
  expectedShopDomain: string,
): Promise<StockyDataChildren> {
  const encodedRecordId = encodeStockyDataRecordId(resource, recordId);
  const value = await stockyDataFetch<unknown>(
    `${ROOT}/${resource}/${encodedRecordId}/children`,
    {
      expectedShopDomain,
      query: {
        limit: input.limit ?? 50,
        cursor: input.cursor ?? undefined,
      },
    },
  );
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky data children");
  }
  const response = value as StockyDataChildren & {
    parent_id: unknown;
    records: unknown;
    page: unknown;
  };
  if (
    response.resource !== resource ||
    !isStockyDataChildResource(resource) ||
    !Array.isArray(response.records)
  ) {
    throw new TypeError("invalid Stocky data children");
  }
  return {
    ...response,
    records: response.records.map((record) =>
      normalizeStockyDataChildRecord(resource, record),
    ),
    page: normalizeStockyDataPage(response.page),
    parent_id: canonicalStockyDataRecordId(resource, response.parent_id),
    generic_source: normalizeStockyDataGenericSource(response.generic_source),
  };
}

export async function listStockyNoteRevisions(
  resource: "suppliers" | "purchase-orders",
  recordId: StockyDataRecordId,
  input: { limit?: number; cursor?: string | null },
  expectedShopDomain: string,
): Promise<StockyNoteRevisionList> {
  const encodedRecordId = encodeStockyDataRecordId(resource, recordId);
  const value = await stockyDataFetch<unknown>(
    `${ROOT}/${resource}/${encodedRecordId}/note-revisions`,
    {
      expectedShopDomain,
      query: {
        limit: input.limit ?? 50,
        cursor: input.cursor ?? undefined,
      },
    },
  );
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky note revision list");
  }
  const response = value as StockyNoteRevisionList & {
    record_id: unknown;
    revisions: unknown;
    page: unknown;
  };
  if (response.resource !== resource || !Array.isArray(response.revisions)) {
    throw new TypeError("invalid Stocky note revision list");
  }
  return {
    resource,
    record_id: canonicalStockyDataRecordId(resource, response.record_id),
    revisions: response.revisions.map(normalizeStockyNoteRevision),
    page: normalizeStockyDataPage(response.page),
  };
}

export async function createStockyNoteRevision(
  resource: "suppliers" | "purchase-orders",
  recordId: StockyDataRecordId,
  input: CreateStockyNoteRevisionInput,
  idempotencyKey: string,
  expectedShopDomain: string,
): Promise<CreateStockyNoteRevisionResponse> {
  const encodedRecordId = encodeStockyDataRecordId(resource, recordId);
  const value = await stockyDataFetch<unknown>(
    `${ROOT}/${resource}/${encodedRecordId}/note-revisions`,
    {
      method: "POST",
      body: input,
      headers: { "Idempotency-Key": idempotencyKey },
      expectedShopDomain,
    },
  );
  if (value === null || typeof value !== "object") {
    throw new TypeError("invalid Stocky note revision response");
  }
  const response = value as CreateStockyNoteRevisionResponse & {
    revision: unknown;
  };
  return {
    revision: normalizeStockyNoteRevision(response.revision),
    replayed: response.replayed,
  };
}

export function listStockyProductMismatches(
  input: {
    limit?: number;
    afterId?: StockyPublicBigIntId | null;
    q?: string;
    status?: StockyProductMismatchStatus;
  },
  expectedShopDomain: string,
): Promise<StockyProductMismatchList> {
  const afterId =
    input.afterId === null || input.afterId === undefined
      ? undefined
      : canonicalStockyPublicBigIntId(input.afterId);
  return stockyDataFetch<unknown>(
    `${ROOT}/product-mismatches`,
    {
      expectedShopDomain,
      query: {
        limit: input.limit ?? 50,
        after_id: afterId,
        q: input.q?.trim() || undefined,
        status: input.status ?? "pending",
      },
    },
    STOCKY_MISMATCH_DRAFT_CONTRACT_VERSION,
  ).then(normalizeStockyProductMismatchList);
}

function canonicalStockyMismatchDraftInput(
  input: CreateStockyMismatchDraftProductInput,
): CreateStockyMismatchDraftProductInput {
  return {
    source: normalizeStockyProductIdentity(input.source),
    source_snapshot_revision: canonicalStockyMismatchSnapshotRevision(
      input.source_snapshot_revision,
    ),
    expected_mapping_revision: canonicalStockyMappingRevision(
      input.expected_mapping_revision,
    ),
  };
}

function normalizeStockyMismatchDraftProductResponse(
  value: unknown,
  sourceVariantId: string,
  input: CreateStockyMismatchDraftProductInput,
  expectedClaimToken: string | null = null,
): StockyMismatchDraftProductResponse {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("invalid Stocky mismatch draft response");
  }
  const response = value as StockyMismatchDraftProductResponse & {
    source_variant_id: unknown;
    source_snapshot_revision: unknown;
    expected_mapping_revision: unknown;
    draft_product: unknown;
  };
  const responseSourceId = canonicalStockyPublicBigIntId(
    response.source_variant_id,
  );
  const sourceSnapshotRevision = canonicalStockyMismatchSnapshotRevision(
    response.source_snapshot_revision,
  );
  const expectedMappingRevision = canonicalStockyMappingRevision(
    response.expected_mapping_revision,
  );
  const draftProduct = normalizeStockyMismatchDraftProduct(
    response.draft_product,
  );
  if (
    responseSourceId !== sourceVariantId ||
    BigInt(sourceSnapshotRevision) < BigInt(input.source_snapshot_revision) ||
    expectedMappingRevision < input.expected_mapping_revision ||
    typeof response.mapping_written !== "boolean" ||
    response.inventory_written !== false ||
    response.quantity_written !== false ||
    (expectedClaimToken !== null &&
      draftProduct.claim_token !== expectedClaimToken)
  ) {
    throw new TypeError("inconsistent Stocky mismatch draft response");
  }
  if (
    response.mapping_written &&
    (draftProduct.state !== "catalog_synced" || draftProduct.current_target === null)
  ) {
    throw new TypeError("auto-mapped DRAFT requires an exact catalog checkpoint");
  }
  return {
    source_variant_id: responseSourceId,
    source_snapshot_revision: sourceSnapshotRevision,
    expected_mapping_revision: expectedMappingRevision,
    draft_product: draftProduct,
    mapping_written: response.mapping_written,
    inventory_written: false,
    quantity_written: false,
  };
}

/**
 * Create or reconcile one server-owned Shopify DRAFT for the exact retained
 * mismatch snapshot. It never writes inventory; a later exact catalog-sync for
 * a v2 claim appends only a receipt-bound mapping (never operational settings).
 */
export function createOrReconcileStockyMismatchDraftProduct(
  sourceVariantId: StockyPublicBigIntId,
  input: CreateStockyMismatchDraftProductInput,
  expectedShopDomain: string,
): Promise<StockyMismatchDraftProductResponse> {
  const canonicalSourceVariantId =
    canonicalStockyPublicBigIntId(sourceVariantId);
  const canonicalInput = canonicalStockyMismatchDraftInput(input);
  return stockyDataFetch<unknown>(
    `${ROOT}/product-mismatches/${encodeURIComponent(canonicalSourceVariantId)}/draft-product`,
    {
      method: "POST",
      body: canonicalInput,
      expectedShopDomain,
    },
    STOCKY_MISMATCH_DRAFT_CONTRACT_VERSION,
  ).then((value) =>
    normalizeStockyMismatchDraftProductResponse(
      value,
      canonicalSourceVariantId,
      canonicalInput,
    ),
  );
}

/**
 * After the merchant explicitly confirms the exact DRAFT was saved, fetch and
 * upsert only the receipt-owned Shopify product and variant. A verified v2
 * receipt is then auto-mapped to that exact variant; operational settings,
 * unrelated catalog rows, and inventory quantities stay untouched.
 */
export function syncStockyMismatchDraftProductToCatalog(
  sourceVariantId: StockyPublicBigIntId,
  input: SyncStockyMismatchDraftProductInput,
  expectedShopDomain: string,
): Promise<StockyMismatchDraftCatalogSyncResponse> {
  const canonicalSourceVariantId =
    canonicalStockyPublicBigIntId(sourceVariantId);
  const canonicalInput = canonicalStockyMismatchDraftInput(input);
  const body: SyncStockyMismatchDraftProductInput = {
    ...canonicalInput,
    claim_token: canonicalStockyMismatchDraftClaimToken(input.claim_token),
  };
  return stockyDataFetch<unknown>(
    `${ROOT}/product-mismatches/${encodeURIComponent(canonicalSourceVariantId)}/draft-product/catalog-sync`,
    {
      method: "POST",
      body,
      expectedShopDomain,
    },
    STOCKY_MISMATCH_DRAFT_CONTRACT_VERSION,
  ).then((value) =>
    normalizeStockyMismatchDraftProductResponse(
      value,
      canonicalSourceVariantId,
      canonicalInput,
      body.claim_token,
    ),
  );
}

/**
 * Explicit one-click upgrade for an old v1 catalog-synced claim.  There is no
 * target argument by design: the backend can map only the exact target already
 * recorded in the verified DRAFT receipt, and it never creates/searches a
 * Shopify product or applies settings/inventory.
 */
export function completeLegacyStockyMismatchDraftCatalogMapping(
  sourceVariantId: StockyPublicBigIntId,
  input: CompleteLegacyStockyMismatchDraftMappingInput,
  expectedShopDomain: string,
): Promise<StockyMismatchDraftProductResponse> {
  const canonicalSourceVariantId =
    canonicalStockyPublicBigIntId(sourceVariantId);
  const canonicalInput = canonicalStockyMismatchDraftInput(input);
  const body: CompleteLegacyStockyMismatchDraftMappingInput = {
    ...canonicalInput,
    claim_token: canonicalStockyMismatchDraftClaimToken(input.claim_token),
  };
  return stockyDataFetch<unknown>(
    `${ROOT}/product-mismatches/${encodeURIComponent(canonicalSourceVariantId)}/draft-product/complete-auto-mapping`,
    {
      method: "POST",
      body,
      expectedShopDomain,
    },
    STOCKY_MISMATCH_DRAFT_CONTRACT_VERSION,
  ).then((value) =>
    normalizeStockyMismatchDraftProductResponse(
      value,
      canonicalSourceVariantId,
      canonicalInput,
      body.claim_token,
    ),
  );
}

export function searchStockyCurrentVariants(
  q: string,
  expectedShopDomain: string,
  limit = 20,
): Promise<StockyCurrentVariantSearch> {
  return stockyDataFetch<unknown>(`${ROOT}/current-variants`, {
    expectedShopDomain,
    query: { q: q.trim(), limit },
  }).then(normalizeStockyCurrentVariantSearch);
}

export function listStockyProductMappingRevisions(
  sourceVariantId: StockyPublicBigIntId,
  expectedShopDomain: string,
): Promise<StockyProductMappingRevisionList> {
  const canonicalSourceVariantId =
    canonicalStockyPublicBigIntId(sourceVariantId);
  return stockyDataFetch<unknown>(
    `${ROOT}/product-mismatches/${encodeURIComponent(canonicalSourceVariantId)}/mapping-revisions`,
    { expectedShopDomain },
    STOCKY_MISMATCH_DRAFT_CONTRACT_VERSION,
  ).then(normalizeStockyProductMappingRevisionList);
}

export function createStockyProductMappingRevision(
  sourceVariantId: StockyPublicBigIntId,
  input: CreateStockyProductMappingInput,
  idempotencyKey: string,
  expectedShopDomain: string,
): Promise<CreateStockyProductMappingResponse> {
  const canonicalSourceVariantId =
    canonicalStockyPublicBigIntId(sourceVariantId);
  const canonicalInput: CreateStockyProductMappingInput = {
    ...input,
    target_variant_id:
      input.target_variant_id === null
        ? null
        : canonicalStockyPublicBigIntId(input.target_variant_id),
  };
  const body =
    canonicalInput.decision === "mapped"
      ? {
          ...canonicalInput,
          operational_application_contract: "copy-once-v1" as const,
          apply_operational_settings: true as const,
        }
      : canonicalInput;
  return stockyDataFetch<unknown>(
    `${ROOT}/product-mismatches/${encodeURIComponent(canonicalSourceVariantId)}/mapping-revisions`,
    {
      method: "POST",
      body,
      headers: { "Idempotency-Key": idempotencyKey },
      expectedShopDomain,
    },
    STOCKY_PRODUCT_MAPPING_CONTRACT_VERSION,
  ).then(normalizeCreateStockyProductMappingResponse);
}
